import { createQueryParams } from "features/config/helpers";
import {
  useAppDispatch,
  useAppSelector,
  useQuery,
} from "features/config/hooks";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Button from "@mui/material/Button";

import {
  Column,
  DataTable,
  DataTableCell,
  DataTableRow,
} from "features/shared/presentation/components/data-table";

import {
  DataList,
  MaterialInput,
  MaterialInputAutoComplete,
  MaterialSwitch,
} from "features/shared/presentation/components";

import {
  getStockOrderSettingProducts,
  resetGetStockOrderSettingProductsStatus,
  selectGetStockOrderSettingProducts,
} from "../slices/stock-order-get-settings-products.slice";
import { AiFillFolderAdd } from "react-icons/ai";

import {
  openMessageModal,
  closeMessageModal,
} from "features/shared/presentation/slices/message-modal.slice";
import { PiceChangeHistory } from "../modals";
import { getProductCostChangeHistory } from "../slices/get-product-cost-change-history.slice";
import { UpdateStock } from "../modals/update-stock.modal";
import { selectUpdateNewProductStock } from "../slices/update-new-product-stock.slice";
import { MenuItem } from "@mui/material";
import {
  selectStockOrderSettingsProductInfoChanges,
  stockOrderSettingsProductInfoChanges,
} from "../slices/stock-order-settings-product-change-info.slice";

interface FormState {
  stockStatValue: string | null | undefined;
}

export function StockOrderSettingsProductContents() {
  let columns: Array<Column> = [
    // { id: "id", label: "id" },
    { id: "product_id", label: "Product Id" },
    { id: "product_name", label: "Product Name" },
    { id: "current_stock", label: "Current Stock" },
    { id: "uom", label: "UOM" },
    { id: "category_name", label: "Category Name" },
    { id: "cost", label: "Cost" },
    // { id: "active_status", label: "Active Status" },
    { id: "", label: "Action" },
  ];

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const getStockOrderSettingsProductsState = useAppSelector(
    selectGetStockOrderSettingProducts
  );
  const updateStockOrderProductActiveStatus = useAppSelector(
    selectStockOrderSettingsProductInfoChanges
  );

  const updateNewProductStockState = useAppSelector(
    selectUpdateNewProductStock
  );

  const [openPriceChange, setOpenPriceChange] = useState(false);
  const [openUpdateStock, setOpenUpdateStock] = useState(false);

  const query = useQuery();
  const pageNo = query.get("page_no");
  const perPage = query.get("per_page");
  const orderBy = query.get("order_by");
  const order = query.get("order");

  const search = query.get("search");
  const status = query.get("status");

  useEffect(() => {
    const query = createQueryParams({
      page_no: pageNo,
      per_page: perPage,
      order_by: orderBy,
      order: order,
      search: search,
    });

    dispatch(getStockOrderSettingProducts(query));
  }, [
    dispatch,
    updateNewProductStockState,
    pageNo,
    perPage,
    orderBy,
    order,
    search,
    updateStockOrderProductActiveStatus,
  ]);

  const handleStockStatusChange =
    (productData: { productId: string; warehouseId: number }) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { productId, warehouseId } = productData;

      dispatch(
        openMessageModal({
          message: "Are you sure you want change the status of this product",
          buttons: [
            {
              color: "#CC5801",
              text: "Yes",
              onClick: () => {
                dispatch(
                  stockOrderSettingsProductInfoChanges({
                    changeType: "status",
                    payload: {
                      productId: productId,
                      warehouseId: warehouseId,
                      product_stock_status: e.target.value,
                    },
                  })
                );
                dispatch(closeMessageModal());
              },
            },
            {
              color: "#22201A",
              text: "No",
              onClick: () => {
                dispatch(closeMessageModal());
              },
            },
          ],
        })
      );
    };

  return (
    <>
      <div className="space-y-3">
        <div className="flex flex-col px-4 lg:flex-row lg:items-end">
          <span className="text-secondary text-3xl font-['Bebas_Neue'] flex-1">
            Products
          </span>
          <div className="flex flex-col space-y-1 lg:flex-row lg:space-x-4 lg:space-y-0">
            <div>
              <Link
                to="create"
                className="inline-flex items-center px-4 tracking-wide py-1  bg-button font-['Varela_Round'] text-white text-xs rounded-md font-700"
              >
                <AiFillFolderAdd size={20} />
                <span>&nbsp;&nbsp;Create a new Product</span>
              </Link>
            </div>
          </div>
        </div>

        <div className="hidden md:block">
          <DataTable
            order={order === "asc" ? "asc" : "desc"}
            orderBy={orderBy ?? "last_updated"}
            search={search ?? ""}
            emptyMessage={`"No products."`}
            onSearch={(val) => {
              const params = {
                page_no: null,
                per_page: perPage,
                status: status,
                order_by: orderBy,
                order: order,
                search: val === "" ? null : val,
              };
              const queryParams = createQueryParams(params);
              navigate({
                pathname: "",
                search: queryParams,
              });
            }}
            onRequestSort={(column_selected) => {
              if (column_selected !== "action") {
                const isAsc = orderBy === column_selected && order === "asc";

                const params = {
                  page_no: pageNo,
                  per_page: perPage,
                  status: status,
                  order_by: column_selected,
                  order: isAsc ? "desc" : "asc",
                  search: search,
                };

                const queryParams = createQueryParams(params);

                dispatch(resetGetStockOrderSettingProductsStatus());
                navigate({
                  pathname: "",
                  search: queryParams,
                });
              }
            }}
            columns={columns}
            onRowsPerPageChange={(event) => {
              if (perPage !== event.target.value) {
                const params = {
                  page_no: pageNo,
                  per_page: event.target.value,
                  status: status,
                  order_by: orderBy,
                  order: order,
                  search: search,
                };

                const queryParams = createQueryParams(params);

                dispatch(resetGetStockOrderSettingProductsStatus());
                navigate({
                  pathname: "",
                  search: queryParams,
                });
              }
            }}
            onPageChange={(event, newPage) => {
              const pageNoInt = pageNo ? parseInt(pageNo) : null;
              if (newPage !== pageNoInt) {
                const params = {
                  page_no: newPage,
                  per_page: perPage,
                  status: status,
                  order_by: orderBy,
                  order: order,
                  search: search,
                };

                const queryParams = createQueryParams(params);

                dispatch(resetGetStockOrderSettingProductsStatus());
                navigate({
                  pathname: "",
                  search: queryParams,
                });
              }
            }}
            totalRows={
              getStockOrderSettingsProductsState.data?.pagination.total_rows ??
              1
            }
            perPage={
              getStockOrderSettingsProductsState.data?.pagination.per_page ?? 25
            }
            page={pageNo ? parseInt(pageNo) : 1}
          >
            {getStockOrderSettingsProductsState.data?.products.map(
              (product) => (
                <DataTableRow>
                  {/* <DataTableCell>{product.id}</DataTableCell> */}
                  <DataTableCell>{product.product_id}</DataTableCell>
                  <DataTableCell>{product.product_name}</DataTableCell>
                  <DataTableCell>{product.current_stock}</DataTableCell>
                  <DataTableCell>{product.uom}</DataTableCell>
                  <DataTableCell>{product.category_name}</DataTableCell>
                  <DataTableCell>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="grid grid-rows-2 gap-2">
                        <span className="text-[#08457E] font-semibold">
                          Manila Cost:
                        </span>
                        <span className="text-[#08457E] font-semibold">
                          Province Cost:
                        </span>
                      </div>
                      <div className="grid grid-rows-2 gap-2">
                        <span className="text-[#85BB65]">{`₱${product.manila_cost}`}</span>
                        <span className="text-[#85BB65]">{`₱${product.province_cost}`}</span>
                      </div>
                    </div>
                  </DataTableCell>

                  {/* 
                  REMOVING ACTIVE STATUS CELL BECAUSE IM MIGRATING IT TO MANY STATUSES.
                  THIS REMOVE STATUS POINTS INTO PRODUCT TB NOT THE PRODUCT STOCKS OF SUPPPLIER */}

                  {/*                     
                  <DataTableCell>
                    <MaterialSwitch
                      label=""
                      checked={product.active_status === 1 ? true : false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        dispatch(
                          openMessageModal({
                            message: `Are you sure you want to ${
                              checked ? "enable" : "disable"
                            } this product ?`,
                            buttons: [
                              {
                                color: "#CC5801",
                                text: "Yes",
                                onClick: () => {
                                  dispatch(
                                    stockOrderSettingsProductInfoChanges({
                                      changeType: "active-status",
                                      payload: {
                                        productId: product.id,
                                        active_status: checked,
                                      },
                                    })
                                  );
                                  dispatch(closeMessageModal());
                                },
                              },
                              {
                                color: "#22201A",
                                text: "No",
                                onClick: () => {
                                  dispatch(closeMessageModal());
                                },
                              },
                            ],
                          })
                        );
                      }}
                    />
                  </DataTableCell> */}

                  <DataTableCell>
                    <div className="flex flex-col space-y-1">
                      <MaterialInput
                        colorTheme="black"
                        name="stockStat"
                        required
                        select
                        value={product.stock_status}
                        onChange={handleStockStatusChange({
                          productId: product.product_id,
                          warehouseId: product.warehouse_id,
                        })}
                        className="flex-1"
                        size="small"
                      >
                        {getStockOrderSettingsProductsState.data?.stock_status.map(
                          (stockStat) => (
                            <MenuItem value={stockStat.id}>
                              {stockStat.status_name}
                            </MenuItem>
                          )
                        )}
                      </MaterialInput>
                      <Link
                        to={`edit/${product.id}`}
                        className="flex justify-center p-1 text-white font-[Roboto] font-medium tracking-[0.025rem] border-2 bg-button border-button rounded-[4px] shadow-md hover:bg-primary hover:border-primary"
                      >
                        <span className="capitalize text-xs">Change Info</span>
                      </Link>

                      {/* <Button
                        variant="contained"
                        size="small"
                        sx={{
                          backgroundColor: "rgba(204, 88, 1, 1)",
                        }}
                        onClick={() => {
                          const productData = createQueryParams({
                            productId: product.product_id,
                            manilaCost: product.manila_cost,
                            provinceCost: product.province_cost,
                          });

                          const productHistoryQueryParam = createQueryParams({
                            productId: product.product_id,
                          });

                          dispatch(
                            getProductCostChangeHistory(
                              productHistoryQueryParam
                            )
                          );

                          navigate({
                            pathname: "",
                            search: productData,
                          });

                          setOpenPriceChange(true);
                        }}
                      >
                        <span className="capitalize text-xs">change price</span>
                      </Button> */}

                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          backgroundColor: "rgba(204, 88, 1, 1)",
                        }}
                        onClick={() => {
                          const productData = createQueryParams({
                            productId: product.product_id,
                          });

                          navigate({
                            pathname: "",
                            search: productData,
                          });

                          setOpenUpdateStock(true);
                        }}
                      >
                        <span className="capitalize text-xs">Update Stock</span>
                      </Button>
                    </div>
                  </DataTableCell>
                </DataTableRow>
              )
            )}
          </DataTable>
        </div>

        <div className="block md:hidden">
          <DataList
            search={search ?? ""}
            emptyMessage="No products"
            onSearch={(val) => {
              const params = {
                page_no: null,
                per_page: perPage,
                status: status,
                order_by: orderBy,
                order: order,
                search: val === "" ? null : val,
              };

              const queryParams = createQueryParams(params);

              navigate({
                pathname: "",
                search: queryParams,
              });
            }}
            onRowsPerPageChange={(event) => {
              if (perPage !== event.target.value) {
                const params = {
                  page_no: pageNo,
                  per_page: event.target.value,
                  order_by: orderBy,
                  order: order,
                  search: search,
                };

                const queryParams = createQueryParams(params);

                dispatch(resetGetStockOrderSettingProductsStatus());
                navigate({
                  pathname: "",
                  search: queryParams,
                });
              }
            }}
            onPageChange={(event, newPage) => {
              const pageNoInt = pageNo ? parseInt(pageNo) : null;
              if (newPage !== pageNoInt) {
                const params = {
                  page_no: newPage,
                  per_page: perPage,
                  order_by: orderBy,
                  order: order,
                  search: search,
                };

                const queryParams = createQueryParams(params);

                dispatch(resetGetStockOrderSettingProductsStatus());
                navigate({
                  pathname: "",
                  search: queryParams,
                });
              }
            }}
            totalRows={
              getStockOrderSettingsProductsState.data?.pagination.total_rows ??
              1
            }
            perPage={
              getStockOrderSettingsProductsState.data?.pagination.per_page ?? 25
            }
            page={pageNo ? parseInt(pageNo) : 1}
          >
            {getStockOrderSettingsProductsState.data?.products.map(
              (product) => (
                <div className="space-y-2 mt-2">
                  <div className="flex flex-col border border-gray-200 rounded-md shadow-sm p-2 bg-white">
                    <div className="flex justify-between text-normal">
                      <span className="normal-case">
                        {product.product_name}
                      </span>
                      <span>{product.product_name}</span>
                    </div>
                    <div className="text-sm">
                      <span className="lowercase">{product.uom}</span>
                    </div>

                    <div className="text-xs capitalize space-x-1">
                      <span>{product.category_id}</span>
                    </div>

                    <div className="text-xs capitalize space-x-1">
                      {/* <span>{product.cost}</span> */}
                    </div>
                  </div>
                </div>
              )
            )}
          </DataList>
        </div>
      </div>

      <PiceChangeHistory
        open={openPriceChange}
        onClose={() => setOpenPriceChange(false)}
      />

      <UpdateStock
        open={openUpdateStock}
        onClose={() => setOpenUpdateStock(false)}
      />
    </>
  );
}
function stockOrderActiveStatus(arg0: {}): any {
  throw new Error("Function not implemented.");
}
